/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';

import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';
import user from 'js/lib/user';

import { InlineNotification, LoadingSection, Pagination, Typography2 as Typography } from '@coursera/cds-core';

import { submitResponse } from 'bundles/reviews-common/utils/ReviewHelpfulAPIUtils';
import ReviewTableCard, { getReviewAuthorName } from 'bundles/reviews/components/ReviewTableCard';
import ReviewsTableToolbar from 'bundles/reviews/components/ReviewsTableToolbar';
import type {
  AllCourseReviews,
  AllCourseReviewsVariables,
  AllCourseReviews_ProductReviewsV1Resource_reviews_elements,
} from 'bundles/reviews/components/__generated__/AllCourseReviews';
import COURSE_REVIEW_QUERY from 'bundles/reviews/components/queries/AllCourseReviews';

import _t from 'i18n!nls/reviews';

const MAX_START_PLUS_LIMIT = 10000;

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_VALUES = [10, 20, 30, 40, 50];

const styles = {
  root: css`
    padding: var(--cds-spacing-200) 0;
    margin-bottom: var(--cds-spacing-400);
  `,
  header: css`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
  `,
  cardList: css`
    margin-bottom: var(--cds-spacing-400);
  `,
  loading: css`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  `,
  pagination: css`
    display: flex;
    justify-content: center;
    margin-top: var(--cds-spacing-400);
  `,
};

type ReviewsTableProps = {
  courseId: string;
};

export type ReviewWithAuthor = AllCourseReviews_ProductReviewsV1Resource_reviews_elements & {
  fullName?: string | null;
  users?: { publicDemographics?: { fullName?: string | null } | null } | null;
};

export function ReviewsTable({ courseId }: ReviewsTableProps) {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [ratingValues, setRatingValues] = React.useState<number[]>([1, 2, 3, 4, 5]);
  const [productCompleted, setProductCompleted] = React.useState<boolean | undefined>(undefined);
  const [sortByHelpfulVotes, setSortByHelpfulVotes] = React.useState(true);

  const executeAction = useFluxibleAction();
  const effectiveUserId = React.useMemo(() => {
    return user.isAuthenticatedUser() ? user.get().id : undefined;
  }, []);

  const startNum = (page - 1) * pageSize;
  const overLimit = startNum + pageSize >= MAX_START_PLUS_LIMIT;
  const start = String(overLimit ? MAX_START_PLUS_LIMIT - pageSize : startNum);

  const variables: AllCourseReviewsVariables = {
    courseId,
    limit: pageSize,
    start,
    ratingValues,
    sortByHelpfulVotes,
  };
  if (productCompleted === true) {
    variables.productCompleted = true;
  }

  const { data, loading, error } = useQuery<AllCourseReviews, AllCourseReviewsVariables>(COURSE_REVIEW_QUERY, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const reviews: ReviewWithAuthor[] =
    data?.ProductReviewsV1Resource?.reviews?.elements?.filter(
      (review): review is NonNullable<typeof review> => review != null && getReviewAuthorName(review) != null
    ) ?? [];
  const total = Number(data?.ProductReviewsV1Resource?.reviews?.paging?.total ?? 0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const cappedPage = overLimit ? totalPages : page;

  const handleLearnerChange = React.useCallback((value: boolean) => {
    setProductCompleted(value);
    setPage(1);
  }, []);

  const handleStarChange = React.useCallback((value: number[]) => {
    setRatingValues(value);
    setPage(1);
  }, []);

  const handleSortChange = React.useCallback((helpful: boolean) => {
    setSortByHelpfulVotes(helpful);
    setPage(1);
  }, []);

  const handlePageChange = React.useCallback((_e: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = React.useCallback((_e: React.ChangeEvent<unknown>, newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  }, []);

  const handleMarkHelpful = React.useCallback(
    (reviewId: string, voteType: string) => {
      if (!effectiveUserId || !executeAction) return;
      executeAction(submitResponse, {
        reviewData: { userId: effectiveUserId, reviewId },
        voteType,
        onSuccess: () => {},
      });
    },
    [executeAction, effectiveUserId]
  );

  const pageSizeInputOptions = React.useMemo(
    () =>
      PAGE_SIZE_VALUES.map((value) => ({
        label: _t('#{count} results per page', { count: value }),
        value,
      })),
    []
  );

  if (loading && !data) {
    return (
      <div css={[styles.root, styles.loading]} data-testid="reviews-table-loading">
        <LoadingSection />
      </div>
    );
  }

  if (error) {
    return (
      <div css={styles.root} data-testid="reviews-table-error">
        <InlineNotification severity="error" title={_t('Unable to load reviews')} children={error.message} />
      </div>
    );
  }

  const displayTotal = total.toLocaleString();
  const showingLabel = _t('Showing: #{count} of #{total}', {
    count: reviews.length,
    total: displayTotal,
  });

  return (
    <section css={styles.root} data-testid="reviews-table" aria-busy={loading}>
      <header css={styles.header} data-testid="reviews-table-header">
        <Typography variant="subtitleLarge" component="h2">
          {_t('All reviews')}
        </Typography>
        <Typography variant="bodySecondary" color="supportText" component="p">
          {showingLabel}
        </Typography>
      </header>

      <ReviewsTableToolbar
        productCompleted={productCompleted}
        ratingValues={ratingValues}
        sortByHelpfulVotes={sortByHelpfulVotes}
        onLearnerChange={handleLearnerChange}
        onStarChange={handleStarChange}
        onSortChange={handleSortChange}
      />

      <div role="status" aria-live="polite">
        {loading ? _t('Loading reviews') : null}
      </div>

      {loading ? (
        <div css={styles.loading} data-testid="reviews-table-loading">
          <LoadingSection />
        </div>
      ) : reviews.length === 0 ? (
        <Typography variant="bodyPrimary" color="supportText" data-testid="reviews-table-empty" component="p">
          {_t('No reviews found')}
        </Typography>
      ) : (
        <div css={styles.cardList} data-testid="reviews-table-list">
          {reviews.map((review) => (
            <ReviewTableCard
              key={review?.id}
              review={review}
              userId={effectiveUserId}
              onMarkHelpful={handleMarkHelpful}
            />
          ))}
        </div>
      )}

      {total > 0 && (
        <div css={styles.pagination} data-testid="reviews-table-pagination">
          <Pagination
            total={total}
            page={cappedPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeInputOptions={pageSizeInputOptions}
            showXsNav
            alignment="justified"
          />
        </div>
      )}
    </section>
  );
}

export default ReviewsTable;
